/**
 * Zdielane konstanty a pomocne funkcie frontendu.
 *
 * Vyclenene z App.js 2026-08-05. Pouziva ich App, MapView aj TopologyView.
 * CE je skratka za React.createElement - cely frontend je pisany bez JSX.
 */
import React from 'react';

export var CE = React.createElement;

var PING_OPTS = [
  [0.17, '10 sekúnd ⚡'],
  [0.5,  '30 sekúnd ⚡'],
  [1,    '1 minúta'],
  [2,    '2 minúty'],
  [5,    '5 minút'],
  [10,   '10 minút'],
  [15,   '15 minút'],
  [30,   '30 minút'],
  [60,   '1 hodina']
];
function pingLabel(pm) {
  pm = parseFloat(pm);
  if (!pm) return '10 min';
  if (pm < 1) return Math.round(pm * 60) + ' s';
  return pm + ' min';
}

// Format seconds to human-readable uptime string
function formatUptime(seconds) {
  var s = Math.round(Number(seconds) || 0);
  var days = Math.floor(s / 86400);
  var hours = Math.floor((s % 86400) / 3600);
  var mins = Math.floor((s % 3600) / 60);
  if (days > 0) return days + 'd ' + hours + 'h ' + mins + 'm';
  if (hours > 0) return hours + 'h ' + mins + 'm';
  return mins + 'm';
}

// Format an OID live value for display (handles timeticks, normal numbers)
// Ciselne stavove kody -> citatelny text. Kluc je zaciatok OID, hodnota je
// mapa kod -> popis. Bez tohto by v UI boli len cisla typu "2".
var OID_KODY = [
  // UBNT EdgePower - zdroje (…1.5.3.2.1.<stlpec>.<index>)
  ['1.3.6.1.4.1.41112.1.5.3.2.1.3.',  { 0: 'neznámy', 1: '✅ zapnutý', 2: '⛔ vypnutý', 3: '⏸️ pohotovosť' }],
  ['1.3.6.1.4.1.41112.1.5.3.2.1.4.',  { 0: '⛔ nefunguje', 1: '✅ funguje' }],
  ['1.3.6.1.4.1.41112.1.5.3.2.1.7.',  { 0: 'neznáme', 1: '🔌 nabíja', 2: 'nenabíja (plná)' }],
  ['1.3.6.1.4.1.41112.1.5.3.2.1.11.', { 0: 'neznáme', 1: '✅ v poriadku', 2: '⚠️ vymeniť' }],
  ['1.3.6.1.4.1.41112.1.5.3.2.1.2.',  { 0: 'neznámy', 1: 'sieť 230V', 2: 'batéria', 3: 'PoE' }],
  // RACOM RAy3 (enterprises 33555, ray3 = .4)
  ['1.3.6.1.4.1.33555.4.1.3.1.',  { 0: 'n/a', 1: '✅ v poriadku', 2: '⚠️ varovanie', 3: '⛔ alarm' }],
  ['1.3.6.1.4.1.33555.4.1.3.8.',  { 0: 'n/a', 1: 'nastavovanie', 2: 'samostatná', 3: '🔄 pripája sa',
                                    4: 'autorizácia', 5: '✅ spojenie OK', 6: 'analyzátor' }],
  ['1.3.6.1.4.1.33555.4.1.3.9.',  { 0: 'n/a', 1: '✅ hore', 2: '⛔ dole' }],
  ['1.3.6.1.4.1.33555.4.1.3.10.', { 0: 'n/a', 1: '✅ hore', 2: '⛔ dole' }],
  // AlarmState — POZOR: up(1) znamena ALARM AKTIVNY, down(2) je v poriadku
  ['1.3.6.1.4.1.33555.4.1.8.',    { 0: 'n/a', 1: '🔴 ALARM', 2: '✅ v poriadku', 3: '🟠 alarm potvrdený' }],
  // ModulationList — vyssie cislo = lepsia modulacia = lepsi spoj
  ['1.3.6.1.4.1.33555.4.2.1.7.',  { 0: 'n/a', 1: 'QPSK-S', 2: 'QPSK', 3: 'QAM16', 4: 'QAM32',
                                    5: 'QAM64', 6: 'QAM128', 7: 'QAM256', 8: 'QAM512',
                                    9: 'QAM1024', 10: 'QAM2048', 11: 'QAM4096' }],
  // IF-MIB ifOperStatus
  ['1.3.6.1.2.1.2.2.1.8.',            { 1: '✅ hore', 2: '⛔ dole', 3: 'testovanie', 5: 'spí', 6: 'nepripojené' }],
];

function popisKodu(oid, val) {
  if (!oid) return null;
  for (var i = 0; i < OID_KODY.length; i++) {
    if (oid.indexOf(OID_KODY[i][0]) === 0) {
      var t = OID_KODY[i][1][Math.round(val)];
      return t ? t + ' (' + Math.round(val) + ')' : null;
    }
  }
  return null;
}

function formatOidValue(val, unit, oid) {
  if (val == null) return null;
  // ticks = SNMP Timeticks stored as seconds after monitor.py conversion
  if (unit === 'ticks' || unit === 's' || unit === 'uptime') {
    return formatUptime(val);
  }
  var kod = popisKodu(oid, val);
  if (kod) return kod;
  var n = val * 1;
  // Show integer if no decimal part, otherwise 2 decimals
  return (Number.isInteger(n) ? String(n) : n.toFixed(2)) + (unit ? '\u00A0' + unit : '');
}

var S = {
  app: { fontFamily: "'Segoe UI', Arial, sans-serif", background: '#0f172a', minHeight: '100vh', color: '#e2e8f0', padding: '24px' },
  header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', borderBottom: '1px solid #1e293b', paddingBottom: '16px' },
  title: { fontSize: '22px', fontWeight: '700', color: '#38bdf8', margin: 0 },
  badge: { background: '#1e293b', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', color: '#94a3b8' },
  refreshBtn: { marginLeft: 'auto', background: '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #1e293b', paddingBottom: '0' },
  tab: function(active) {
    return { padding: '8px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', border: 'none',
      borderBottom: active ? '2px solid #38bdf8' : '2px solid transparent',
      background: 'transparent', color: active ? '#38bdf8' : '#64748b', marginBottom: '-1px' };
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '24px' },
  card: function(c) { return { background: '#1e293b', borderRadius: '10px', padding: '18px', borderLeft: '4px solid ' + c }; },
  cardTitle: { fontSize: '11px', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' },
  cardValue: { fontSize: '28px', fontWeight: '700', color: '#f1f5f9' },
  panel: { background: '#1e293b', borderRadius: '10px', padding: '20px', marginBottom: '20px' },
  panelTitle: { fontSize: '12px', color: '#64748b', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.05em' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '8px 10px', color: '#64748b', borderBottom: '1px solid #334155', fontSize: '11px', textTransform: 'uppercase' },
  td: { padding: '10px 10px', borderBottom: '1px solid #1e293b', verticalAlign: 'middle' },
  dot: function(ok) {
    var c = ok === null ? '#64748b' : ok ? '#22c55e' : '#ef4444';
    return { display: 'inline-block', width: '9px', height: '9px', borderRadius: '50%', background: c, marginRight: '7px' };
  },
  upBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', background: '#14532d', color: '#86efac' },
  downBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', background: '#7f1d1d', color: '#fca5a5' },
  alertBox: { background: '#451a03', border: '1px solid #f59e0b', borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', fontSize: '13px', color: '#fde68a' },
  note: { color: '#475569', fontSize: '12px', marginTop: '10px' },
  mapWrap: { borderRadius: '10px', overflow: 'hidden', marginBottom: '20px' },
  mapNote: { background: '#1e293b', borderRadius: '8px', padding: '12px 16px', fontSize: '12px', color: '#64748b', marginBottom: '12px' },
};

export { PING_OPTS, pingLabel, formatUptime, formatOidValue, popisKodu, S };
